import { useMemo } from "react";

import { Chip, Stack, Typography, useTheme } from "@mui/material";

import { ErrorOutlineRounded } from "@mui/icons-material";

import { ChevronRightIcon } from "igfd-ui/src";

import { staffMenuCopy } from "../../../menu/staff-menu-copy";
import { INDENT_STEP } from "../../../menu/staff-menu-tree";

interface DishProps {
  title: string;
  description: string;
  highlight?: string;
  hasDivider: boolean;
  /** Groups this dish owns. They live in its drawer, so the row only counts them. */
  choiceCount?: number;
  /** Anything under this dish we could not read, at any depth. */
  unreadableCount?: number;
  /** Where a search result sits: nearest ancestor first, section last. */
  trail?: string[];
  /** Steps in under a sub-heading, for a group with no ancestor dish. */
  indent?: number;
  onClick: () => void;
}

/** A search term is a user string, and `(` alone makes an invalid pattern. */
const escapeForRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const Dish = ({
  title,
  description,
  highlight,
  hasDivider,
  choiceCount = 0,
  unreadableCount = 0,
  trail,
  indent = 0,
  onClick,
}: DishProps) => {
  const {
    palette: { oscar },
    typography,
  } = useTheme();

  const emphasise = useMemo(() => {
    if (!highlight) return (value: string) => value;
    const pattern = new RegExp(`(${escapeForRegExp(highlight)})`, "ig");
    return (value: string) =>
      value?.replace(
        pattern,
        `<span style='
              font-family: ${typography.labelLHeavy?.fontFamily};
              font-size: ${typography.labelLHeavy?.fontSize};
              color: ${oscar.light.content.link};
          '>$1</span>`
      );
  }, [highlight, typography, oscar]);

  const highlightedTitle = useMemo(() => emphasise(title), [emphasise, title]);
  const highlightedDescription = useMemo(
    () => emphasise(description),
    [emphasise, description]
  );

  return (
    <Stack
      alignItems="flex-start"
      flexDirection="row"
      justifyContent="space-between"
      sx={{
        cursor: "pointer",
        paddingLeft: indent * INDENT_STEP,
      }}
      paddingY={3}
      borderBottom={
        hasDivider ? `1px solid ${oscar.light.borders.gray}` : undefined
      }
      onClick={onClick}
    >
      <Stack gap={1} minWidth={0}>
        {/* A bare "Garlic aioli" in a result list is the bug this all exists to
            fix, so a result says what it belongs to before anything else. */}
        {trail?.length ? (
          <Typography variant="labelS" color={oscar.light.content.content1}>
            {trail.join(" · ")}
          </Typography>
        ) : null}
        <Typography
          variant="labelLMedium"
          dangerouslySetInnerHTML={{ __html: highlightedTitle }}
        />
        {description ? (
          <Typography
            variant="bodyRLight"
            dangerouslySetInnerHTML={{ __html: highlightedDescription }}
          />
        ) : null}
        {choiceCount > 0 || unreadableCount > 0 ? (
          <Stack direction="row" gap={1} flexWrap="wrap" paddingTop={0.5}>
            {choiceCount > 0 ? (
              <Chip
                label={staffMenuCopy.choices(choiceCount)}
                size="small"
                sx={{
                  backgroundColor: oscar.light.surface.gray,
                  color: oscar.light.content.content1,
                  ...typography.labelSMedium,
                }}
              />
            ) : null}
            {/* Grey, not red. Unknown is a reason to ask the kitchen, and
                spending the alarm colour here devalues it where it means harm. */}
            {unreadableCount > 0 ? (
              <Chip
                icon={
                  <ErrorOutlineRounded
                    sx={{ color: `${oscar.light.icon.dark} !important` }}
                  />
                }
                label={staffMenuCopy.unreadable(unreadableCount)}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: oscar.light.borders.gray,
                  color: oscar.light.content.content1,
                  ...typography.labelSMedium,
                }}
              />
            ) : null}
          </Stack>
        ) : null}
      </Stack>

      <ChevronRightIcon
        height={16}
        width={16}
        color={oscar.light.content.link}
      />
    </Stack>
  );
};
